import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Notice, TextInput, Stack, Text } from '@vello/web-theme';
import {
  type BiFilterState,
  type UrlState,
  biErrorView,
  decodeUrlState,
  filterRequestParts,
  readToken,
  shareUrl,
  stateFromParts,
  toUrlState,
  withToken,
} from '../../../src/index';
import { useAdmin } from '../../session';
import { useConsole } from '../../console';
import { type BiText } from './bi-ui';

/** The conditions of a URL state (the part the server checks again when a link is opened). */
function builderOf(state: UrlState): BiFilterState {
  return state.kind === 'panels' ? state.bar : { filters: state.explorer.filters, anyOf: state.explorer.anyOf, having: state.explorer.having };
}

function withBuilder(state: UrlState, builder: BiFilterState): UrlState {
  return state.kind === 'panels' ? { ...state, bar: { ...builder, having: [] } } : { kind: 'explorer', explorer: { ...state.explorer, filters: builder.filters, anyOf: builder.anyOf, having: builder.having } };
}

export interface UrlSync {
  /** The link is being checked by the server (nothing is asked until it is). */
  readonly restoring: boolean;
  /** The id of the message to show when the link could not be opened (unreadable, another version, refused by the server). */
  readonly problem: string | undefined;
  /** Dimensions whose personal conditions were left out when the link was opened (the link never carries people). */
  readonly stripped: readonly string[];
  readonly dismiss: () => void;
}

/**
 * The state of a screen in its URL (D528): on open, the link is decoded here and then CHECKED BY THE SERVER (the planner's parser, the
 * caller's own reach, personal values stripped again) before anything is applied - a link grants nothing; afterwards the address bar follows the
 * screen's state (`history.replaceState`, so the back button is not filled with every click).
 */
export function useUrlSync<S extends UrlState>({ ready, current, onRestore, kind }: { ready: boolean; current: S | undefined; onRestore: (state: S) => void; kind: S['kind'] }): UrlSync {
  const { runtime } = useAdmin();
  const [phase, setPhase] = useState<'pending' | 'checking' | 'done'>('pending');
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [stripped, setStripped] = useState<readonly string[]>([]);
  const restore = useRef(onRestore);
  restore.current = onRestore;

  // ---- open: decode, ask the server, apply what it accepts ----
  useEffect(() => {
    if (!ready || phase !== 'pending') return undefined;
    const token = typeof window !== 'undefined' ? readToken(window.location.search) : undefined;
    if (token === undefined) {
      setPhase('done');
      return undefined;
    }
    const decoded = decodeUrlState(token);
    const forget = (): void => window.history.replaceState(null, '', `${window.location.pathname}${withToken(window.location.search, undefined)}${window.location.hash}`);
    if (!decoded.ok) {
      setProblem(`bi.url.error.${decoded.error}`);
      forget();
      setPhase('done');
      return undefined;
    }
    if (decoded.state.kind !== kind) {
      setPhase('done');
      return undefined;
    }
    setPhase('checking');
    let live = true;
    void runtime.analyticsBi.checkFilters({ state: filterRequestParts(builderOf(decoded.state)) }).then((res) => {
      if (!live) return;
      if (!res.ok) {
        setProblem(biErrorView(res.error).messageId);
        forget();
      } else {
        // What the SERVER accepted replaces what the link said; whatever it stripped is said aloud.
        setStripped(res.value.stripped);
        const accepted = stateFromParts(res.value.state ?? {});
        restore.current(withBuilder(decoded.state, accepted) as S);
      }
      setPhase('done');
    });
    return () => {
      live = false;
    };
  }, [ready, phase, kind, runtime]);

  // ---- follow: the address bar mirrors the applied state ----
  const mirror = useMemo(() => (current === undefined ? undefined : toUrlState(current).token), [current]);
  useEffect(() => {
    if (phase !== 'done' || typeof window === 'undefined') return;
    const search = withToken(window.location.search, mirror);
    if (search === window.location.search) return;
    window.history.replaceState(null, '', `${window.location.pathname}${search}${window.location.hash}`);
  }, [mirror, phase]);

  return { restoring: phase === 'checking', problem, stripped, dismiss: () => { setProblem(undefined); setStripped([]); } };
}

/** The notices a screen shows about its link: why one did not open, which people were left out of it. */
export function UrlNotices({ sync, text }: { sync: UrlSync; text: BiText }): ReactElement | null {
  const { t } = text;
  if (sync.problem === undefined && sync.stripped.length === 0 && !sync.restoring) return null;
  return (
    <Stack gap={2}>
      {sync.restoring && <Notice tone="neutral">{t('bi.url.checking')}</Notice>}
      {sync.problem !== undefined && <Notice tone="warning">{t(sync.problem)}</Notice>}
      {sync.stripped.length > 0 && <Notice tone="neutral">{t('bi.url.stripped', { dimensions: sync.stripped.map(text.dimName).join(', ') })}</Notice>}
    </Stack>
  );
}

/** "Copy link": the screen's question as a link, never carrying a person; where the clipboard is blocked the link is shown to copy by hand. */
export function ShareLinkButton({ state, text }: { state: UrlState | undefined; text: BiText }): ReactElement {
  const { t } = text;
  const { toast } = useConsole();
  const [shown, setShown] = useState<string | undefined>(undefined);
  const made = useMemo(() => (state === undefined ? undefined : toUrlState(state)), [state]);
  const tooLong = made !== undefined && made.token === undefined;
  const copy = async (): Promise<void> => {
    if (made?.token === undefined || typeof window === 'undefined') return;
    const url = shareUrl(window.location, made.token);
    try {
      await window.navigator.clipboard.writeText(url);
      setShown(undefined);
      toast({ tone: 'success', message: t('bi.url.copied') });
    } catch {
      setShown(url);
    }
  };
  return (
    <Stack gap={2}>
      <div>
        <Button variant="secondary" disabled={made?.token === undefined} onClick={() => void copy()}>
          {t('bi.url.copy')}
        </Button>
      </div>
      {tooLong && <Text role="caption" tone="secondary">{t('bi.url.tooLong')}</Text>}
      {made !== undefined && made.stripped.length > 0 && <Text role="caption" tone="secondary">{t('bi.url.willStrip', { dimensions: made.stripped.map(text.dimName).join(', ') })}</Text>}
      {shown !== undefined && <TextInput readOnly aria-label={t('bi.url.manual')} value={shown} onFocus={(e) => e.currentTarget.select()} />}
    </Stack>
  );
}
